import Matter from "matter-js";

const {
	Engine,
	Render,
	Runner,
	Bodies,
	Composite,
	Mouse,
	MouseConstraint,
	Body,
	Query,
} = Matter;

export class HeroPhysics {
	constructor(canvas) {
		this.canvas = canvas;
		this.container = canvas.closest(".intro");

		// Guard: ensure container exists
		if (!this.container) {
			console.warn("HeroPhysics: .intro container not found");
			return;
		}

		// Get stroke colors from CSS variables
		const style = getComputedStyle(document.documentElement);
		this.accentColor =
			style.getPropertyValue("--color-accent").trim() || "#FF5C00";
		this.defaultColor =
			style.getPropertyValue("--green-dark").trim() || "#1c3b37";
		this.strokeWidth = 1;
		this.scale = 1;

		// Store bound handlers for cleanup
		this.handleResizeBound = this.handleResize.bind(this);
		this.handleVisibilityBound = this.handleVisibility.bind(this);
		this.drawCustomShapesBound = this.drawCustomShapes.bind(this);

		// Track custom bodies for rendering
		this.customBodies = [];
		this.walls = [];

		this.init();
		this.setupEventListeners();
	}

	getDimensions() {
		const rect = this.container.getBoundingClientRect();
		return {
			width: rect.width,
			height: rect.height,
		};
	}

	init() {
		const { width, height } = this.getDimensions();

		// Set canvas size explicitly
		this.canvas.style.width = `${width}px`;
		this.canvas.style.height = `${height}px`;

		this.engine = Engine.create();
		this.engine.world.gravity.y = 1;

		this.render = Render.create({
			canvas: this.canvas,
			engine: this.engine,
			options: {
				width,
				height,
				wireframes: false,
				background: "transparent",
				pixelRatio: window.devicePixelRatio || 1,
			},
		});

		this.createBoundaries(width, height);
		this.createShapes(width);
		this.setupMouseInteraction();

		// Custom rendering for plus shape
		Matter.Events.on(this.render, "afterRender", this.drawCustomShapesBound);

		Render.run(this.render);
		this.runner = Runner.create();
		Runner.run(this.runner, this.engine);
	}

	drawCustomShapes() {
		const ctx = this.render.context;
		ctx.strokeStyle = this.accentColor;
		ctx.lineWidth = this.strokeWidth;

		for (const body of this.customBodies) {
			if (!body.shapeData) continue;

			ctx.save();
			ctx.translate(body.position.x, body.position.y);
			ctx.rotate(body.angle);

			const { type, size, thickness } = body.shapeData;

			ctx.beginPath();
			if (type === "plus") {
				const h = size / 2;
				const t = thickness / 2;
				ctx.moveTo(-t, -h);
				ctx.lineTo(t, -h);
				ctx.lineTo(t, -t);
				ctx.lineTo(h, -t);
				ctx.lineTo(h, t);
				ctx.lineTo(t, t);
				ctx.lineTo(t, h);
				ctx.lineTo(-t, h);
				ctx.lineTo(-t, t);
				ctx.lineTo(-h, t);
				ctx.lineTo(-h, -t);
				ctx.lineTo(-t, -t);
				ctx.closePath();
			} else if (type === "square") {
				const h = size / 2;
				ctx.rect(-h, -h, size, size);
			} else if (type === "pencil") {
				const verts = body.shapeData.vertices;
				ctx.moveTo(verts[0].x, verts[0].y);
				for (let i = 1; i < verts.length; i++) {
					ctx.lineTo(verts[i].x, verts[i].y);
				}
				ctx.closePath();
			} else if (type === "equilateralTriangle") {
				const r = size / 2;
				ctx.moveTo(0, -r);
				ctx.lineTo(r * 0.866, r * 0.5);
				ctx.lineTo(-r * 0.866, r * 0.5);
				ctx.closePath();
			} else if (type === "circle") {
				ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
			} else if (type === "pill") {
				const { width, height, radius } = body.shapeData;
				const halfW = (width - height) / 2;
				ctx.moveTo(-halfW, -radius);
				ctx.lineTo(halfW, -radius);
				ctx.arc(halfW, 0, radius, -Math.PI / 2, Math.PI / 2);
				ctx.lineTo(-halfW, radius);
				ctx.arc(-halfW, 0, radius, Math.PI / 2, -Math.PI / 2);
				ctx.closePath();
			}
			ctx.stroke();

			ctx.restore();
		}
	}

	createBoundaries(width, height) {
		// Remove old walls
		if (this.walls.length) {
			Composite.remove(this.engine.world, this.walls);
		}

		const thickness = 100;
		const padding = 25;
		const wallOptions = {
			isStatic: true,
			render: { visible: false },
		};

		this.walls = [
			// Floor
			Bodies.rectangle(
				width / 2,
				height + thickness / 2 - padding,
				width * 2,
				thickness,
				wallOptions
			),
			// Left wall
			Bodies.rectangle(
				-thickness / 2 + padding,
				height / 2,
				thickness,
				height * 2,
				wallOptions
			),
			// Right wall
			Bodies.rectangle(
				width + thickness / 2 - padding,
				height / 2,
				thickness,
				height * 2,
				wallOptions
			),
		];

		Composite.add(this.engine.world, this.walls);
	}

	createShapes(width) {
		const s = this.scale;
		const size = 300 * s;

		const physicsOptions = {
			restitution: 0,
			friction: 1,
			frictionAir: 0.02,
		};

		// Plus always spawns (logo)
		const plus = this.createPlus(size * 1, 25 * s, physicsOptions);

		// Other shapes to pick from
		const otherCreators = [
			() => this.createSquare(size * 0.7, physicsOptions),
			() => this.createCircle(size * 0.8, physicsOptions),
			() => this.createPencil(size * 1, physicsOptions),
			() => this.createEquilateralTriangle(size * 1, physicsOptions),
			() => this.createPill(size * 1, physicsOptions),
		];

		// Pick 3 random shapes + plus
		const shuffled = otherCreators.sort(() => Math.random() - 0.5);
		const shapes = [plus, ...shuffled.slice(0, 4).map((create) => create())];

		// Store custom bodies for rendering
		this.customBodies = shapes;

		shapes.forEach((shape, i) => {
			if (shape) {
				// Spawn in right 33% of canvas, stagger Y to avoid overlap
				const x = Math.random() * width * 0.28 + width * 0.67;
				const y = -size - i * size * 1.2;
				const angle = Math.random() * Math.PI * 2;

				Body.setPosition(shape, { x, y });
				Body.setAngle(shape, angle);
			}
		});

		Composite.add(this.engine.world, shapes);
	}

	createSquare(size, options) {
		const body = Bodies.rectangle(0, 0, size, size, {
			...options,
			render: { visible: false },
		});
		body.shapeData = { type: "square", size };
		return body;
	}

	createCircle(size, options) {
		const body = Bodies.circle(0, 0, size / 2, {
			...options,
			render: { visible: false },
		});
		body.shapeData = { type: "circle", size };
		return body;
	}

	createPencil(size, options) {
		const w = size * 0.4; // narrower width
		const h = size / 2;
		const tipHeight = size * 0.25;
		const vertices = [
			{ x: -w / 2, y: h },
			{ x: -w / 2, y: -h + tipHeight },
			{ x: 0, y: -h },
			{ x: w / 2, y: -h + tipHeight },
			{ x: w / 2, y: h },
		];
		const body = Bodies.fromVertices(0, 0, vertices, {
			...options,
			render: { visible: false },
		});
		const cx = body.position.x;
		const cy = body.position.y;
		body.shapeData = {
			type: "pencil",
			vertices: body.vertices.map((v) => ({ x: v.x - cx, y: v.y - cy })),
		};
		return body;
	}

	createPlus(size, thickness, options) {
		const armLength = (size - thickness) / 2;
		const offset = thickness / 2 + armLength / 2;

		const parts = [
			Bodies.rectangle(0, 0, thickness, thickness, {
				render: { visible: false },
			}),
			Bodies.rectangle(0, -offset, thickness, armLength, {
				render: { visible: false },
			}),
			Bodies.rectangle(0, offset, thickness, armLength, {
				render: { visible: false },
			}),
			Bodies.rectangle(-offset, 0, armLength, thickness, {
				render: { visible: false },
			}),
			Bodies.rectangle(offset, 0, armLength, thickness, {
				render: { visible: false },
			}),
		];

		const body = Body.create({
			parts,
			...options,
		});
		Body.setInertia(body, body.inertia * 2);
		body.shapeData = { type: "plus", size, thickness };
		return body;
	}

	createEquilateralTriangle(size, options) {
		const r = size / 2;
		const vertices = [
			{ x: 0, y: -r },
			{ x: r * 0.866, y: r * 0.5 },
			{ x: -r * 0.866, y: r * 0.5 },
		];
		const body = Bodies.fromVertices(0, 0, vertices, {
			...options,
			render: { visible: false },
		});
		body.shapeData = { type: "equilateralTriangle", size };
		return body;
	}

	createPill(size, options) {
		const width = size;
		const height = size * 0.4;
		const radius = height / 2;

		// Compound body: rectangle + two circles for rounded ends
		const parts = [
			Bodies.rectangle(0, 0, width - height, height, {
				render: { visible: false },
			}),
			Bodies.circle(-(width - height) / 2, 0, radius, {
				render: { visible: false },
			}),
			Bodies.circle((width - height) / 2, 0, radius, {
				render: { visible: false },
			}),
		];

		const body = Body.create({
			parts,
			...options,
		});
		body.shapeData = { type: "pill", width, height, radius };
		return body;
	}

	setupMouseInteraction() {
		const mouse = Mouse.create(this.canvas);
		mouse.pixelRatio = window.devicePixelRatio || 1;

		// Allow scrolling through the canvas
		mouse.element.removeEventListener("wheel", mouse.mousewheel);

		this.mouseConstraint = MouseConstraint.create(this.engine, {
			mouse,
			constraint: {
				stiffness: 0.8,
				damping: 0.3,
				render: { visible: false },
			},
		});

		// Cursor changes for drag interaction
		Matter.Events.on(this.mouseConstraint, "startdrag", () => {
			this.canvas.style.cursor = "grabbing";
		});
		Matter.Events.on(this.mouseConstraint, "enddrag", () => {
			this.canvas.style.cursor = "grab";
		});

		// Detect hover over shapes
		this.canvas.addEventListener("mousemove", (e) => {
			if (this.mouseConstraint.constraint.bodyB) return; // dragging
			const rect = this.canvas.getBoundingClientRect();
			const point = {
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			};
			const bodies = Composite.allBodies(this.engine.world).filter(
				(b) => !b.isStatic
			);
			const hoveredBodies = Query.point(bodies, point);
			this.canvas.style.cursor = hoveredBodies.length ? "grab" : "";
		});

		Composite.add(this.engine.world, this.mouseConstraint);
		this.render.mouse = mouse;
	}

	setupEventListeners() {
		this.resizeTimeout = null;
		window.addEventListener("resize", this.handleResizeBound);
		document.addEventListener("visibilitychange", this.handleVisibilityBound);
	}

	handleVisibility() {
		if (document.hidden) {
			Runner.stop(this.runner);
			Render.stop(this.render);
		} else {
			Render.run(this.render);
			Runner.run(this.runner, this.engine);
		}
	}

	handleResize() {
		clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => {
			const { width, height } = this.getDimensions();

			// Update canvas size
			this.canvas.style.width = `${width}px`;
			this.canvas.style.height = `${height}px`;
			this.render.canvas.width = width * (window.devicePixelRatio || 1);
			this.render.canvas.height = height * (window.devicePixelRatio || 1);
			this.render.options.width = width;
			this.render.options.height = height;

			// Only update boundaries, keep shapes in place
			this.createBoundaries(width, height);
		}, 200);
	}

	destroy() {
		// Remove event listeners
		window.removeEventListener("resize", this.handleResizeBound);
		document.removeEventListener(
			"visibilitychange",
			this.handleVisibilityBound
		);
		clearTimeout(this.resizeTimeout);

		// Remove Matter.js event
		Matter.Events.off(this.render, "afterRender", this.drawCustomShapesBound);

		// Stop and clear
		Render.stop(this.render);
		Runner.stop(this.runner);
		Engine.clear(this.engine);
	}
}
